import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Heartbeat, CheckCircle, Warning, XCircle, ArrowClockwise, Key, FileArchive, Files, Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  healthy: {
    label: '正常',
    Icon: CheckCircle,
    accent: 'from-emerald-500 to-teal-600',
    ring: 'ring-emerald-200',
    bg: 'bg-emerald-50',
    border: 'border-emerald-300',
    text: 'text-emerald-900',
    dot: 'bg-emerald-500',
  },
  warning: {
    label: '要確認',
    Icon: Warning,
    accent: 'from-amber-500 to-orange-600',
    ring: 'ring-amber-200',
    bg: 'bg-amber-50',
    border: 'border-amber-300',
    text: 'text-amber-900',
    dot: 'bg-amber-500',
  },
  critical: {
    label: '緊急',
    Icon: XCircle,
    accent: 'from-rose-500 to-red-600',
    ring: 'ring-rose-200',
    bg: 'bg-rose-50',
    border: 'border-rose-300',
    text: 'text-rose-900',
    dot: 'bg-rose-500',
  },
};

const fmtHours = (h) => {
  if (h == null) return '未実行';
  if (h < 1) return `${Math.round(h * 60)} 分前`;
  if (h < 24) return `${h.toFixed(1)} 時間前`;
  return `${(h / 24).toFixed(1)} 日前`;
};

const fmtIso = (iso) => {
  if (!iso) return '—';
  return formatDateTime(iso) || iso;
};

/**
 * Backup Health Widget — compact card that answers "did the nightly cron
 * actually run?" in one glance. Sits at the top of /settings/backup.
 *
 * Colored by status:
 *   healthy  (< 24h since last export) — green
 *   warning  (24h - 48h)                 — amber
 *   critical (> 48h OR never)            — rose
 *
 * Data source: GET /api/settings/backup/health — reads persisted token
 * usage stats, no live storage listing (fast).
 */
export function BackupHealthWidget() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await axios.get(`${API}/settings/backup/health`, { withCredentials: true });
      setHealth(r.data);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'バックアップ状態を取得できません');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading && !health) {
    return (
      <div className="rounded-xl border border-zinc-200 bg-white shadow-sm px-5 py-4 flex items-center gap-3" data-testid="backup-health-loading">
        <ArrowClockwise size={16} weight="duotone" className="animate-spin text-zinc-500" />
        <span className="text-[13px] text-zinc-500">バックアップ状態を確認中…</span>
      </div>
    );
  }

  if (!health) return null;

  const meta = STATUS_META[health.status] || STATUS_META.critical;
  const isNever = health.hours_since_last_backup == null;

  return (
    <div
      className={`rounded-xl border-2 ${meta.border} ${meta.bg} shadow-sm overflow-hidden`}
      data-testid="backup-health-widget"
      data-status={health.status}
    >
      <div className="px-5 py-4 flex items-start gap-4">
        {/* Status ring */}
        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${meta.accent} text-white flex items-center justify-center ring-4 ${meta.ring} shrink-0 shadow`}>
          <meta.Icon size={30} weight="duotone" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className={`font-bold ${meta.text} text-[15px]`}>
              バックアップ状態
            </h2>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.border} ${meta.text} bg-white/70`}
              data-testid="backup-health-status-badge"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
              {meta.label}
            </span>
            <button
              type="button"
              onClick={() => load({ silent: true })}
              disabled={refreshing}
              className="ml-auto text-[10px] text-zinc-500 hover:text-zinc-800 inline-flex items-center gap-1"
              title="再取得"
              data-testid="backup-health-refresh"
            >
              <ArrowClockwise size={11} className={refreshing ? 'animate-spin' : ''} />
              再取得
            </button>
          </div>

          <div className={`text-[13px] mt-1 ${meta.text}`}>
            {isNever ? (
              <span data-testid="backup-health-never">
                <b>まだバックアップが取得されていません。</b>
                下の「NAS 自動取得トークン」でトークンを発行し、cron を設定してください。
              </span>
            ) : (
              <span data-testid="backup-health-summary">
                最終取得: <b data-testid="backup-health-last-relative">{fmtHours(health.hours_since_last_backup)}</b>
                {' · '}
                <span className="text-zinc-500 text-[11px] font-mono" data-testid="backup-health-last-absolute">
                  {fmtIso(health.last_export_at)}
                </span>
              </span>
            )}
          </div>

          {/* Stats row */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            <StatChip
              icon={Files}
              label="ファイル"
              value={health.last_manifest_files?.toLocaleString() || '0'}
              testid="backup-health-manifest-files"
            />
            <StatChip
              icon={FileArchive}
              label="DB ドキュメント"
              value={health.last_total_documents?.toLocaleString() || '0'}
              testid="backup-health-total-documents"
            />
            <StatChip
              icon={Key}
              label="有効トークン"
              value={`${health.active_tokens} / ${health.total_tokens}`}
              testid="backup-health-active-tokens"
            />
            <StatChip
              icon={Clock}
              label="警告閾値"
              value={`${health.thresholds?.warn_hours || 24}h / ${health.thresholds?.crit_hours || 48}h`}
              testid="backup-health-thresholds"
            />
          </div>

          {health.last_token_label && (
            <div className="text-[10px] text-zinc-500 mt-2" data-testid="backup-health-last-token">
              最終取得トークン: <span className="font-mono">{health.last_token_label}</span>
            </div>
          )}

          {health.status === 'critical' && !isNever && (
            <div
              className={`mt-3 rounded-md bg-white/70 border ${meta.border} px-3 py-2 text-[11px] ${meta.text}`}
              data-testid="backup-health-critical-hint"
            >
              <b>{health.thresholds?.crit_hours || 48} 時間以上</b>バックアップが実行されていません。
              NAS 側の cron が停止していないか、トークンが失効していないか確認してください。
            </div>
          )}
          {health.status === 'warning' && (
            <div
              className={`mt-3 rounded-md bg-white/70 border ${meta.border} px-3 py-2 text-[11px] ${meta.text}`}
              data-testid="backup-health-warning-hint"
            >
              最後のバックアップから <b>{health.thresholds?.warn_hours || 24} 時間以上</b> 経過しています。
              間もなく cron が実行される予定であれば問題ありません。
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function StatChip({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-lg bg-white/70 border border-white px-2.5 py-1.5 flex items-center gap-2" data-testid={testid}>
      <Icon size={14} weight="duotone" className="text-zinc-500 shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="font-bold text-zinc-900 text-[13px] tabular-nums truncate" title={String(value)}>{value}</div>
      </div>
    </div>
  );
}
